import React, { useCallback, useEffect } from 'react';
import useTimelineScale from '@src/hooks/useTimeline';
import { CONTENT_END_PAD_PX } from '@src/constants/timeline';
import {
  StyledContent,
  StyledGrid,
  StyledGutter,
  StyledViewport,
} from './index.style';

export const GUTTER_WIDTH = 80;

/**
 * Layout shell for the timeline: a fixed gutter column + a horizontally
 * scrollable viewport. Knows nothing about tracks — the parent passes the
 * gutter cells via `renderGutter` and the lane content via `children`.
 *
 * The viewport uses native horizontal scroll (browser translates the content —
 * including the playhead line — for free). On scroll we push the position into
 * the context SYNCHRONOUSLY (setScrollLeft only notifies + a bucketed
 * visible-range update, both cheap), so the overlay playhead chip tracks the
 * natively-scrolled content in the same frame with no lag. Viewport width is
 * measured with ResizeObserver so Fit/visible-range math has an accurate value.
 */
const TimelineGrid = ({ renderGutter = null, children = null }) => {
  const { durationSec, secToPx, setScrollLeft, setViewportWidth, viewportRef } =
    useTimelineScale();

  useEffect(() => {
    const el = viewportRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width ?? el.clientWidth;
      setViewportWidth(width);
    });
    observer.observe(el);
    setViewportWidth(el.clientWidth);

    return () => observer.disconnect();
  }, [setViewportWidth]);

  const handleScroll = useCallback(
    (event) => {
      // Push synchronously so the overlay playhead chip stays glued to the
      // natively-scrolled content (no rAF lag). setScrollLeft notifies scroll
      // subscribers and does a bucketed visible-range update that bails when the
      // whole-second window is unchanged, so per-event cost stays low.
      setScrollLeft(event.currentTarget.scrollLeft);
    },
    [setScrollLeft],
  );

  return (
    <StyledGrid>
      <StyledGutter width={GUTTER_WIDTH}>{renderGutter}</StyledGutter>
      <StyledViewport ref={viewportRef} onScroll={handleScroll}>
        <StyledContent contentWidth={secToPx(durationSec) + CONTENT_END_PAD_PX}>
          {children}
        </StyledContent>
      </StyledViewport>
    </StyledGrid>
  );
};

export default TimelineGrid;
